import styled from "styled-components";
import { variant } from "styled-system";
import { apolloFontFamilyButtonLabel, apolloFontSizeButtonLabel, apolloFontWeightButtonLabel, apolloLineHeightButtonLabel, apolloSpaceInset1HalfX, apolloSpaceInset1X, apolloSpaceInset2X, apolloSpaceInsetHalfX, } from "@nexthink/apollo-tokens";
import { getValueFromPixelString } from "../../utils/TokenHelpers";
import { StyledIcon } from "../icons/Icon";
const focusStyle = (theme) => ({
    "&:focus-visible, &.focus": {
        border: `1px solid ${theme.colors.apolloColorStrokeFocus}`,
        boxShadow: `0 0 0 1px ${theme.colors.apolloColorStrokeFocus} inset !important`,
    },
});
const getColor = ({ theme, $variant }) => {
    const color = $variant === "secondary"
        ? theme.colors.apolloColorTextPrimary
        : $variant === "tertiary"
            ? theme.colors.apolloColorTextInteractive
            : theme.colors.apolloColorTextPrimaryOnDark;
    return {
        color,
        [StyledIcon]: {
            color,
        },
    };
};
export const getPadding = (props, horizontalPadding) => {
    const { $pressed } = props;
    const xPadding = horizontalPadding !== null && horizontalPadding !== void 0 ? horizontalPadding : getHorizontalPadding(props);
    return Object.assign({ padding: `0 ${xPadding + ($pressed ? 1 : 0)}px` }, ($pressed && {
        "&:focus-visible, &.focus": {
            padding: `0 ${xPadding}px`,
        },
    }));
};
const getHorizontalPadding = ({ $size }) => $size === "small"
    ? getValueFromPixelString(apolloSpaceInset1X) + getValueFromPixelString(apolloSpaceInsetHalfX)
    : getValueFromPixelString(apolloSpaceInset2X);
const primaryFocusedStyle = (props) => {
    const borderSize = 4;
    const borderRadius = "6px";
    const { theme, $size } = props;
    return {
        "&:focus-visible, &.focus": Object.assign({ border: `${borderSize / 2}px solid ${theme.colors.apolloColorStrokeFocus} !important`, borderRadius: borderRadius, boxShadow: `0 0 0 ${borderSize / 2}px ${theme.colors.apolloColorBackgroundContainer} inset !important`, lineHeight: `calc(${apolloLineHeightButtonLabel} + ${2 * getValueFromPixelString($size === "small" ? apolloSpaceInset1X : apolloSpaceInset1HalfX) - 4}px)` }, getPadding(props, getHorizontalPadding(props) - 1)),
    };
};
export const pressedStyle = (props) => {
    const { theme, $variant = "primary" } = props;
    const focusVisible = {
        "&:focus-visible, &.focus": Object.assign(Object.assign({}, getPadding(props)), { boxShadow: `0 0 0 1px ${theme.colors.apolloColorStrokeFocus} inset !important` }),
    };
    const stylesPerVariant = {
        primary: Object.assign({ background: theme.colors.apolloColorBackgroundButtonPrimaryActive }, primaryFocusedStyle(props)),
        secondary: Object.assign({ background: theme.colors.apolloColorBackgroundFormElementOpen }, focusVisible),
        tertiary: Object.assign({ background: theme.colors.apolloColorBackgroundButtonTertiaryActive, 
            // use the shadow to reproduce the border
            boxShadow: `${theme.colors.apolloBoxShadowFormElementOpen}, inset 0px 0px 0px 1px ${theme.colors.apolloColorStrokeButtonTertiaryActive}` }, focusVisible),
    };
    return Object.assign(Object.assign(Object.assign(Object.assign({}, getColor({ theme, $variant })), { boxShadow: theme.colors.apolloBoxShadowFormElementOpen, border: "none" }), getPadding(props)), stylesPerVariant[$variant]);
};
export const selectedStyle = (props) => {
    const { theme } = props;
    return Object.assign(Object.assign({ bg: theme.colors.apolloColorBackgroundButtonSelected, color: theme.colors.apolloColorTextPrimaryOnDark, border: `1px solid ${theme.colors.apolloColorBackgroundButtonSelected}`, [StyledIcon]: {
            color: theme.colors.apolloColorTextPrimaryOnDark,
        } }, primaryFocusedStyle(props)), { "&:hover, &.hover": {
            bg: theme.colors.apolloColorBackgroundButtonPrimaryHover,
            borderColor: theme.colors.apolloColorBackgroundButtonPrimaryHover,
        }, "&:active, &.active": {
            bg: theme.colors.apolloColorBackgroundButtonPrimaryActive,
            borderColor: theme.colors.apolloColorBackgroundButtonPrimaryActive,
        } });
};
const primaryDisabled = (theme) => ({
    bg: theme.colors.apolloColorBackgroundButtonPrimaryDisabled,
    borderColor: theme.colors.apolloColorBackgroundButtonPrimaryDisabled,
    color: theme.colors.apolloColorTextDisabledOnDark,
    [StyledIcon]: {
        color: theme.colors.apolloColorTextDisabledOnDark,
    },
});
export const primary = (props) => {
    const { theme, disabled, $selected } = props;
    return Object.assign(Object.assign(Object.assign({}, getColor(props)), { bg: $selected
            ? theme.colors.apolloColorBackgroundButtonSelected
            : theme.colors.apolloColorBackgroundButtonPrimaryDefault, border: `1px solid ${theme.colors.apolloColorBackgroundButtonPrimaryDefault}` }), (disabled
        ? primaryDisabled(theme)
        : Object.assign({ "&:hover, &.hover": {
                bg: theme.colors.apolloColorBackgroundButtonPrimaryHover,
                borderColor: theme.colors.apolloColorBackgroundButtonPrimaryHover,
            }, "&:active, &.active": {
                bg: theme.colors.apolloColorBackgroundButtonPrimaryActive,
                borderColor: theme.colors.apolloColorBackgroundButtonPrimaryActive,
            } }, primaryFocusedStyle(props))));
};
const secondaryDisabled = (theme) => ({
    bg: theme.colors.apolloColorBackgroundButtonSecondaryDisabled,
    borderColor: theme.colors.apolloColorBackgroundButtonSecondaryDisabled,
    color: theme.colors.apolloColorTextDisabled,
    boxShadow: "none",
    [StyledIcon]: {
        color: theme.colors.apolloColorTextDisabled,
    },
});
export const secondary = (props) => {
    const { theme, disabled } = props;
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, getColor(props)), { bg: theme.colors.apolloColorBackgroundButtonSecondaryDefault, border: `1px solid ${theme.colors.apolloColorStrokeButtonDefault}` }), focusStyle(theme)), { boxShadow: theme.name === "light" ? theme.colors.apolloBoxShadowFormElement : "none" }), (disabled
        ? secondaryDisabled(theme)
        : {
            "&:hover, &.hover": {
                bg: theme.colors.apolloColorBackgroundButtonSecondaryHover,
                borderColor: theme.colors.apolloColorStrokeButtonHover,
                boxShadow: theme.name === "light" ? theme.colors.apolloBoxShadowFormElement : "none",
            },
            "&:active, &.active": {
                bg: theme.colors.apolloColorBackgroundButtonSecondaryActive,
                borderColor: theme.colors.apolloColorBackgroundButtonSecondaryActive,
                boxShadow: "none",
            },
        }));
};
const tertiaryDisabled = (theme) => ({
    bg: theme.colors.apolloColorBackgroundButtonTertiaryDisabled,
    color: theme.colors.apolloColorTextDisabled,
    border: `1px solid ${theme.colors.apolloColorStrokeButtonTertiaryDisabled}`,
    [StyledIcon]: {
        color: theme.colors.apolloColorTextDisabled,
    },
});
export const tertiary = (props) => {
    const { theme, disabled } = props;
    return Object.assign(Object.assign(Object.assign(Object.assign({}, getColor(props)), focusStyle(theme)), { bg: theme.colors.apolloColorBackgroundButtonTertiaryDefault, border: `1px solid ${theme.colors.apolloColorStrokeButtonTertiaryDefault}` }), (disabled
        ? tertiaryDisabled(theme)
        : {
            "&:hover, &.hover": {
                bg: theme.colors.apolloColorBackgroundButtonTertiaryHover,
                boxShadow: "none",
                borderColor: "transparent",
            },
            "&:active, &.active": {
                bg: theme.colors.apolloColorBackgroundButtonTertiaryActive,
                borderColor: theme.colors.apolloColorStrokeButtonTertiaryActive,
            },
        }));
};
export const baseButton = ({ disabled, $hasIcon }) => (Object.assign(Object.assign({ alignItems: "center", borderRadius: "4px", boxSizing: "border-box", cursor: "pointer", display: "inline-flex", fontFamily: apolloFontFamilyButtonLabel, fontSize: apolloFontSizeButtonLabel, fontWeight: apolloFontWeightButtonLabel, lineHeight: apolloLineHeightButtonLabel, margin: "0", position: "relative", textDecoration: "none", verticalAlign: "middle", whiteSpace: "nowrap" }, (disabled && { cursor: "default" })), ($hasIcon && {
    [StyledIcon]: {
        margin: `0 ${apolloSpaceInsetHalfX} 0 -${apolloSpaceInset1X}`,
        ["&:only-child"]: {
            left: "50%",
            margin: "0",
            position: "absolute",
            transform: "translateX(-50%)",
        },
    },
})));
export const StyledButton = styled.button.withConfig({ displayName: "StyledButton_APC-7311-productShell", componentId: "sc-39uyhx" })((props) => ({ "&&": baseButton(props) }), (props) => variant({
    prop: "$variant",
    variants: {
        primary: { "&&": props.$pressed ? pressedStyle(props) : primary(props) },
        secondary: {
            "&&": props.$selected ? selectedStyle(props) : props.$pressed ? pressedStyle(props) : secondary(props),
        },
        tertiary: {
            "&&": props.$selected ? selectedStyle(props) : props.$pressed ? pressedStyle(props) : tertiary(props),
        },
    },
}), (props) => variant({
    prop: "$size",
    variants: {
        small: {
            "&&": Object.assign({ height: `calc(${apolloLineHeightButtonLabel} + ${2 * getValueFromPixelString(apolloSpaceInset1X)}px)`, lineHeight: `calc(${apolloLineHeightButtonLabel} + ${2 * getValueFromPixelString(apolloSpaceInset1X) - 2}px)` }, (!props.$pressed && getPadding(props))),
        },
        medium: {
            "&&": Object.assign({ height: `calc(${apolloLineHeightButtonLabel} + ${2 * getValueFromPixelString(apolloSpaceInset1HalfX)}px)`, lineHeight: `calc(${apolloLineHeightButtonLabel} + ${2 * getValueFromPixelString(apolloSpaceInset1HalfX) - 2}px)` }, (!props.$pressed && getPadding(props))),
        },
    },
}));
//# sourceMappingURL=Button.style.js.map